import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth, formatApiError } from '@/App';
import { Lightning, EnvelopeSimple, Lock, GoogleLogo } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/217o5tly_Marema_Logo.jpg";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const from = location.state?.from?.pathname || '/portal';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const user = await login(email, password);
      toast.success('Welcome back!');
      
      // Redirect based on role
      if (user.role === 'admin' || user.role === 'staff') {
        navigate('/admin');
      } else {
        navigate('/portal');
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/portal';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0A192F] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0055FF]/20 to-transparent"></div>
        <div className="relative z-10 flex flex-col justify-center px-16">
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img src={LOGO_URL} alt="Marema Consulting" className="h-20 w-auto" />
          </Link>
          
          <h1 className="text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight mb-6">
            Welcome to<br />Marema CRM
          </h1>
          <p className="text-gray-400 text-lg max-w-md">
            Manage client cases, track municipal applications, and streamline your energy consultancy operations.
          </p>

          <div className="mt-12 space-y-4">
            <div className="flex items-center gap-3 text-gray-300">
              <div className="w-2 h-2 bg-[#0055FF] rounded-full"></div>
              <span>Case management & tracking</span>
            </div>
            <div className="flex items-center gap-3 text-gray-300">
              <div className="w-2 h-2 bg-[#0055FF] rounded-full"></div>
              <span>Company & contact database</span>
            </div>
            <div className="flex items-center gap-3 text-gray-300">
              <div className="w-2 h-2 bg-[#0055FF] rounded-full"></div>
              <span>Contract & renewal management</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img src={LOGO_URL} alt="Marema Consulting" className="h-[4.5rem] w-auto" />
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight mb-2">Staff Login</h2>
            <p className="text-[#4B5563]">Access the admin dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-sm font-semibold text-[#0A192F] mb-2 block">Email Address</Label>
              <div className="relative">
                <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4B5563]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@company.com"
                  className="pl-10 border-[#E5E7EB] rounded-none bg-[#F9FAFB] focus:ring-[#0055FF] focus:border-[#0055FF] h-12"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="password" className="text-sm font-semibold text-[#0A192F]">Password</Label>
                <Link to="/forgot-password" className="text-sm text-[#0055FF] hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4B5563]" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  className="pl-10 border-[#E5E7EB] rounded-none bg-[#F9FAFB] focus:ring-[#0055FF] focus:border-[#0055FF] h-12"
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-none h-12 font-medium transition-transform hover:-translate-y-0.5"
              data-testid="login-submit-btn"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#E5E7EB]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-[#F8F9FA] px-4 text-[#4B5563]">Or continue with</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleLogin}
            className="w-full border-2 border-[#E5E7EB] rounded-none h-12 font-medium hover:bg-[#F9FAFB] text-[#0A192F]"
            data-testid="google-login-btn"
          >
            <GoogleLogo className="w-5 h-5 mr-2" weight="bold" />
            Sign in with Google
          </Button>

          <p className="mt-8 text-center text-[#4B5563]">
            Are you a client?{' '}
            <Link to="/portal/login" className="text-[#0055FF] font-medium hover:underline" data-testid="client-login-link">
              Client Portal Login
            </Link>
          </p>

          <Link to="/" className="block mt-6 text-center text-[#4B5563] text-sm hover:text-[#0A192F]">
            &larr; Back to website
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
